import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import Layer from "@arcgis/core/layers/Layer";
import { useContext, useEffect, useState } from "react";
import { FeatureClasses, NauticalClasses } from "../../constants/NauticalObjects";
import { AppContext } from "../../Context";
import { GetCodedValueAndAlias, updateFeatureAttributes } from "../../utils/arcgisUtils";
import { dispatchRefreshFeature, formatDate } from "../../utils/helpers";
import InputField from "../genericComponents/InputField/InputField";
import NfsButton from "../genericComponents/NfsButton/NfsButton";
import NfsDropdown from "../genericComponents/NfsDropdown/NfsDropdown";
import "./FeatureField.css";
import {
    isKystverketFieldMissing,
    resolveKystverketRegionAndRode,
    validateSeamarkNumber,
} from "../../utils/autofillUtils";
import { updateFDVObjectNew } from "../../utils/FDVUtils";
import {
    isHibOrLysboyeSecondary,
    NFSStatus,
    setNewStatusToFeature,
    syncSecondaryFeature,
} from "../../utils/nfsUtils";
import _ from "lodash";
import { AppConfig } from "../../AppConfig";
import { JaNei, SeamarkColors } from "../../constants/nfsDomains";
import { LayerFieldsCommon, LayerFieldsFastSjomerke } from "../../constants/LayerFields";
import { toast } from "react-toastify";
import type CodedValue from "@arcgis/core/layers/support/CodedValue";
import type Graphic from "@arcgis/core/Graphic";

interface FeatureFieldProps {
    isEditing: boolean;
    field: string;
    layer: Layer;
    attributes: Graphic["attributes"];
    feature?: Graphic;
    canBeSentToFdv: boolean;
    secondaryFeature?: Graphic;
    onChange: () => void;
}

const FeatureField = (props: FeatureFieldProps) => {
    const appContext = useContext(AppContext);
    const selectedFeature = appContext?.selectedFeature.value;

    const [fieldValue, setFieldValue] = useState(props.attributes[props.field] ?? "");
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [codedValue, setCodedValue] = useState<string | number>("");
    const [isSaving, setIsSaving] = useState(false);

    // Block new edits while any feature edit is still saving. Prevents overlapping
    // attribute writes that would race the in-flight save and leave the UI showing
    // values that don't match what was actually persisted.
    const editsInFlight = (appContext?.featureEditsInFlight.value ?? 0) > 0;

    //NFS2-1288 Remove generate seamark button
    // const generateAndSetSeamarkNumber = async () => {
    //     if (!props.feature) {
    //         return;
    //     }
    //     try {
    //         setIsLoading(true);
    //         const seamarkNumber = await generateNauticalObjectSeamarkNumber(props.feature);
    //         setFieldValue(seamarkNumber);
    //         setTimeout(() => {
    //             const fieldInput = document.getElementById(props.field) as HTMLInputElement;
    //             if (fieldInput) {
    //                 fieldInput.focus();
    //             }
    //         }, 50);
    //     } finally {
    //         setIsLoading(false);
    //     }
    // };

    const tryValidateSeamarkNumber = async (value: string) => {
        if (!props.feature) {
            return;
        }
        console.log("Validating seamark number:", value);
        setErrorMessage("");
        const errorMessage = await validateSeamarkNumber(value, props.feature);
        if (errorMessage) {
            setErrorMessage(errorMessage);
        }
    };

    const isEditableFeatureClass = (): boolean => {
        return !(
            (props.layer as FeatureLayer).layerId === FeatureClasses.Lyssektor ||
            (props.layer as FeatureLayer).layerId === FeatureClasses.FlytendemerkeTildelt ||
            (props.layer as FeatureLayer).layerId === FeatureClasses.Flytendemerkeinnmålt
        );
    };

    useEffect(() => {
        const isPrimaryFeature = () => {
            return !(
                props.attributes.tilknyttet_hib === JaNei.Ja ||
                props.attributes.tilknyttet_lysboye === JaNei.Ja ||
                props.attributes.tilknyttet_fl_la === JaNei.Ja
            );
        };

        if (props.isEditing && props.field === "sjomerke_nr" && isPrimaryFeature() && isEditableFeatureClass()) {
            const trimmed = fieldValue.toString().trim();
            setFieldValue(trimmed);
            _.debounce(() => {
                tryValidateSeamarkNumber(trimmed);
            }, 200)();
        }
    }, [fieldValue]);

    useEffect(() => {
        setFieldValue(props.attributes[props.field] ?? "");
        setCodedValue(GetCodedValueAndAlias(props.layer, props.field, props.attributes[props.field])[1]);
    }, [props.feature]); // Using attributes instead of only objectid to ensure fields gets updated when changed.

    const updateFdvIfNeeded = async (updatedFeature: Graphic) => {
        // Edits of the secondary feature for HIB or LB also need to be propagated to FDV. However, it needs to be triggered on the primary feature.
        // However, the only attribute that is being synced to FDV from the secondary feature is sjomerke_type, and that one is not editable in the UI.
        // AKA we do nothing about this now.
        if (!props.canBeSentToFdv) {
            return;
        }

        if (updatedFeature.attributes["nfs_status"] !== NFSStatus.PROJECTED) {
            return;
        }

        if (!updatedFeature.attributes["gis_obj_id"]) {
            return;
        }

        try {
            const layer = updatedFeature.layer as FeatureLayer;
            await updateFDVObjectNew(
                {
                    layerId: layer.layerId,
                    globalId: updatedFeature.attributes.globalid,
                    gdbVersion: layer.gdbVersion,
                },
                updatedFeature
            );
            console.log("Feature updated in FDV");
        } catch (error) {
            console.error("Error updating object in FDV:", error);
        }
    };

    const updateSecondaryFeature = async (primaryFeature: Graphic) => {
        if (!props.secondaryFeature) {
            return;
        }
        if (!(props.field in props.secondaryFeature.attributes)) {
            return;
        }
        await syncSecondaryFeature(primaryFeature, props.secondaryFeature);
    };

    const isSecondaryFeatureNonEditableField = (): boolean => {
        if (
            (props.layer as FeatureLayer).layerId === FeatureClasses.IB &&
            (props.attributes.tilknyttet_hib == JaNei.Ja || props.attributes.tilknyttet_fl_la == JaNei.Ja) &&
            (AppConfig.FeatureEditor.SecondaryFeatureCopyAttributeConfig.AllSecondary.includes(props.field) ||
                AppConfig.FeatureEditor.SecondaryFeatureCopyAttributeConfig.IBSecondary.includes(props.field))
        ) {
            return true;
        } else if (
            (props.layer as FeatureLayer).layerId === FeatureClasses.Flytendemerke &&
            props.attributes.tilknyttet_lysboye == JaNei.Ja &&
            AppConfig.FeatureEditor.SecondaryFeatureCopyAttributeConfig.AllSecondary.includes(props.field)
        ) {
            return true;
        } else if (
            isHibOrLysboyeSecondary((props.layer as FeatureLayer).layerId, props.attributes) &&
            AppConfig.FeatureEditor.SecondaryFeatureCopyAttributeConfig.HibLysboyeSecondary.includes(props.field)
        ) {
            return true;
        }
        return false;
    };

    const getRelevantCodedValuesBeskrivelse = (codedValues: CodedValue[]): CodedValue[] => {
        switch (props.attributes.klasse) {
            case NauticalClasses.HIB_HurtigbaatmerkeMedIndirekteBelysning:
                return codedValues.filter((codedValue) => codedValue.name.startsWith("HIB") || codedValue.code === 0);
            case NauticalClasses.FL_Fyrlykt:
                return codedValues.filter(
                    (codedValue) => codedValue.name.startsWith("Lyktehus") || codedValue.code === 0
                );
            case NauticalClasses.IB_IndirekteBelysning:
                return codedValues.filter((codedValue) => codedValue.name.startsWith("IB") || codedValue.code === 0);
            case NauticalClasses.FS_Fyrstasjon:
                return codedValues.filter((codedValue) => codedValue.name.includes("tårn") || codedValue.code === 0);
            case NauticalClasses.LA_Lanterne:
                return codedValues.filter(
                    (codedValue) =>
                        codedValue.name.startsWith("Lanterne") ||
                        codedValue.name.startsWith("Aerolys") ||
                        codedValue.code === 0
                );
            default:
                return codedValues;
        }
    };

    const getRelevantCodedValues = (codedValues: CodedValue[]): CodedValue[] => {
        if (props.field === LayerFieldsCommon.beskrivelse) {
            return getRelevantCodedValuesBeskrivelse(codedValues);
        } else if (props.field === LayerFieldsFastSjomerke.refleks_farge) {
            return codedValues.filter((codedValue) => {
                const intVal = parseInt(`${codedValue.code}`);
                return intVal === SeamarkColors.Red || intVal === SeamarkColors.White || intVal === SeamarkColors.Green;
            });
        }
        return codedValues;
    };

    /** Save a single attribute the same way every other edited field is saved. */
    const persistFieldValue = async (value: string | number) => {
        const updateObject = selectedFeature?.clone();
        if (!updateObject) return;

        updateObject.attributes[props.field] = value;
        selectedFeature!.attributes[props.field] = value;
        updateObject.attributes["objectid"] = props.attributes["objectid"];
        updateObject.attributes["globalid"] = props.attributes["globalid"];
        updateObject.layer = props.layer as FeatureLayer;
        setNewStatusToFeature(updateObject, NFSStatus.PROJECTED_EXISTING);

        appContext?.featureEditsInFlight.begin();
        setIsSaving(true);
        try {
            await updateFeatureAttributes(updateObject);
            await updateSecondaryFeature(updateObject);
            appContext?.selectedFeature.set(updateObject);
            if (appContext?.shownFeature.value?.attributes["objectid"] === props.attributes["objectid"]) {
                appContext?.shownFeature.set(updateObject);
            }
            updateObject.attributes["gis_obj_id"] = props.attributes["gis_obj_id"];
            updateFdvIfNeeded(updateObject);

            const layerId = (props.layer as FeatureLayer)?.layerId;
            const objectId = selectedFeature?.getObjectId();
            if (layerId !== undefined && layerId !== null && objectId !== undefined && objectId !== null) {
                dispatchRefreshFeature(layerId.toString(), objectId as number, false);
            }
            props.onChange();
        } finally {
            appContext?.featureEditsInFlight.end();
            setIsSaving(false);
        }
    };

    /**
     * Region and rode are readonly, but existing objects can be missing them. Offer a
     * one-off lookup from the object's geometry instead of leaving the field permanently
     * blank. Once a value is set the field goes back to being plain readonly text.
     */
    const renderKystverketBackfillButton = () => {
        const isRode = props.field === LayerFieldsCommon.kystverk_rode;
        const name = isRode ? "rode" : "region";

        const fetchAndSave = async () => {
            const feature = props.feature ?? selectedFeature;
            if (!feature) return;

            setIsSaving(true);
            let resolved: { region: number | null; rode: string | null };
            try {
                resolved = await resolveKystverketRegionAndRode(feature);
            } finally {
                setIsSaving(false);
            }

            const value = isRode ? resolved.rode : resolved.region;
            if (value === null) {
                toast.error(`Fant ingen Kystverket-${name} for objektets posisjon.`);
                return;
            }

            await persistFieldValue(value);
        };

        return (
            <NfsButton onClick={fetchAndSave} disabled={editsInFlight || isSaving} small>
                {isSaving ? "Henter..." : `Hent ${name}`}
            </NfsButton>
        );
    };

    const renderEditableField = () => {
        if (
            isKystverketFieldMissing(props.field, props.attributes[props.field]) &&
            !isSecondaryFeatureNonEditableField()
        ) {
            return renderKystverketBackfillButton();
        }

        if (
            AppConfig.FeatureEditor.ReadonlyFields.includes(props.field) ||
            isSecondaryFeatureNonEditableField() ||
            !isEditableFeatureClass()
        ) {
            return renderField();
        }

        if (props.field.endsWith("_h")) {
            return renderField();
        }

        if (!props.layer?.hasOwnProperty("fields")) {
            return renderField();
        }

        const fieldInfo = (props.layer as FeatureLayer)?.fields.find((field) => field.name === props.field);

        if (!fieldInfo) {
            return renderField();
        }

        if (!fieldInfo.editable) {
            return renderField();
        }

        if (
            fieldInfo.hasOwnProperty("domain") &&
            fieldInfo.domain !== null &&
            fieldInfo.domain?.type === "coded-value"
        ) {
            const relevantCodedValues = getRelevantCodedValues(fieldInfo.domain.codedValues);

            const domainOptionCodes = relevantCodedValues.map(
                (codedValue: { name: string; code: any }) => codedValue.code
            );

            return (
                <NfsDropdown
                    options={domainOptionCodes}
                    selectedOption={props.attributes[props.field]}
                    displayValueFunction={(value: number | string) =>
                        GetCodedValueAndAlias(props.layer, props.field, value)[1]
                    }
                    setSelectedOption={async (value) => {
                        const updateObject = selectedFeature?.clone();
                        if (!updateObject) return;
                        updateObject.attributes[props.field] = value;
                        updateObject.attributes["objectid"] = props.attributes["objectid"];
                        updateObject.attributes["globalid"] = props.attributes["globalid"];
                        selectedFeature!.attributes[props.field] = value;
                        updateObject.attributes[props.field] = value;
                        if (Object.hasOwn(props.attributes, "nfs_status")) {
                            updateObject.attributes["nfs_status"] = props.attributes["nfs_status"];
                        }
                        updateObject.layer = props.layer as FeatureLayer;
                        setNewStatusToFeature(updateObject, NFSStatus.PROJECTED_EXISTING);

                        appContext?.featureEditsInFlight.begin();
                        setIsSaving(true);
                        try {
                            const updateResult = await updateFeatureAttributes(updateObject);
                            await updateSecondaryFeature(updateObject);
                            if (updateObject) {
                                updateObject.layer = props.layer as FeatureLayer;
                                appContext?.selectedFeature.set(updateObject);
                                if (
                                    appContext?.shownFeature.value?.attributes["objectid"] ===
                                    props.attributes["objectid"]
                                ) {
                                    appContext?.shownFeature.set(updateObject);
                                }
                            }
                            updateObject.attributes["gis_obj_id"] = props.attributes["gis_obj_id"];

                            updateFdvIfNeeded(updateObject);
                            props.onChange();
                            return updateResult;
                        } finally {
                            appContext?.featureEditsInFlight.end();
                            setIsSaving(false);
                        }
                    }}
                    placeholder={"Ikke definert"}
                    allowEmpty={fieldInfo.nullable}
                    disabled={editsInFlight}
                    isSaving={isSaving}
                />
            );
        }

        let fieldType = null;

        if (fieldInfo.type === "string") {
            fieldType = "text";
        } else if (fieldInfo.type === "double" || fieldInfo.type === "integer") {
            fieldType = "number";
        } else if (fieldInfo.type === "date") {
            fieldType = "date";
        }

        if (fieldType !== null) {
            // NFS2-1288 Remove generate seamark button
            // if (props.field === "sjomerke_nr" && fieldValue === "" && props.attributes["sjomerke_nr"] === null) {
            //     return <NfsButton onClick={generateAndSetSeamarkNumber}>Generér sjømerkenummer</NfsButton>;
            // }

            let rangeMin = undefined;
            let rangeMax = undefined;
            if (fieldInfo.domain?.type === "range") {
                rangeMin = fieldInfo.domain?.minValue;
                rangeMax = fieldInfo.domain?.maxValue;
                // parse to double to avoid issues with string comparison in InputField
                if (typeof rangeMin === "string") {
                    rangeMin = Number.parseFloat(rangeMin);
                }
                if (typeof rangeMax === "string") {
                    rangeMax = Number.parseFloat(rangeMax);
                }
            }

            return (
                <div className="flex-row small-gap w-100">
                    {/* NFS2-1288 Remove generate seamark button */}
                    {/* {props.field === "sjomerke_nr" && props.attributes["nfs_status"] === NFSStatus.PROJECTED && (
                        <NfsButton
                            className="no-flex fit-content padding-0 "
                            title="Generér sjømerkenummer"
                            onClick={generateAndSetSeamarkNumber}
                        >
                            <RedoIcon />
                        </NfsButton>
                    )} */}

                    <InputField
                        inputFieldId={props.field}
                        label=""
                        value={fieldValue}
                        type={fieldType}
                        onChange={(value) => {
                            if (
                                rangeMax !== null &&
                                rangeMin !== null &&
                                rangeMax !== undefined &&
                                rangeMin !== undefined
                            ) {
                                const numericValue = Number(value);
                                if (numericValue > rangeMax) {
                                    setFieldValue(rangeMax);
                                } else if (numericValue < rangeMin) {
                                    setFieldValue(rangeMin);
                                } else {
                                    setFieldValue(value);
                                }
                            } else {
                                setFieldValue(value);
                            }
                        }}
                        min={rangeMin}
                        max={rangeMax}
                        onBlur={async () => {
                            // Normalized original and new values for comparison
                            const original = props.attributes[props.field];
                            if (fieldType === "number") {
                                const originalIsNull = original === null || original === undefined;
                                const newNumeric = fieldValue === "" ? null : Number(fieldValue);
                                // Skip only if both are null/empty OR numeric equal (excluding null -> 0 which should trigger update)
                                const unchanged =
                                    (originalIsNull && newNumeric === null) ||
                                    (!originalIsNull && Number(original) === newNumeric);
                                if (unchanged) {
                                    return;
                                }
                            } else if (fieldType === "text") {
                                if (original === fieldValue) {
                                    return;
                                }
                            } else if (fieldType === "date") {
                                const originalTime =
                                    original === null || original === undefined ? null : new Date(original).getTime();
                                const newTime = fieldValue === "" ? null : new Date(fieldValue).getTime();
                                if (originalTime === newTime) {
                                    return;
                                }
                            }

                            let valueToStore: string | number | null;
                            switch (fieldType) {
                                case "number":
                                    valueToStore = fieldValue === "" ? null : Number(fieldValue);
                                    break;
                                case "text":
                                    valueToStore = fieldValue;
                                    break;
                                case "date":
                                    valueToStore = fieldValue === "" ? null : new Date(fieldValue).getTime();
                                    break;
                                default:
                                    return;
                            }

                            const updateObject = selectedFeature?.clone();
                            if (!updateObject) return false;
                            updateObject.attributes[props.field] = valueToStore;
                            selectedFeature!.attributes[props.field] = valueToStore;
                            updateObject.attributes["objectid"] = props.attributes["objectid"];
                            updateObject.attributes["globalid"] = props.attributes["globalid"];
                            updateObject.layer = props.layer as FeatureLayer;
                            setNewStatusToFeature(updateObject, NFSStatus.PROJECTED_EXISTING);

                            // Track this save as in-flight so the direct-to-default fast path
                            // (which races attribute saves) can't fire while the network call
                            // is still going. Paired in try/finally so the counter is balanced
                            // even if updateFeatureAttributes throws.
                            appContext?.featureEditsInFlight.begin();
                            setIsSaving(true);
                            try {
                                const updateResult = await updateFeatureAttributes(updateObject);
                                await updateSecondaryFeature(updateObject);
                                appContext?.selectedFeature.set(updateObject);
                                if (
                                    appContext?.shownFeature.value?.attributes["objectid"] ===
                                    props.attributes["objectid"]
                                ) {
                                    appContext?.shownFeature.set(updateObject);
                                }
                                updateObject.attributes["gis_obj_id"] = props.attributes["gis_obj_id"];
                                updateFdvIfNeeded(updateObject);

                                const layerId = (props.layer as FeatureLayer)?.layerId;
                                const objectId = selectedFeature?.getObjectId();
                                if (
                                    layerId !== undefined &&
                                    objectId !== undefined &&
                                    layerId !== null &&
                                    objectId !== null
                                ) {
                                    let shouldSearchItemRefresh = false;
                                    if (
                                        props.field === "navn" ||
                                        props.field === "sjomerke_nr" ||
                                        props.field === "sted"
                                    ) {
                                        shouldSearchItemRefresh = true;
                                    }
                                    dispatchRefreshFeature(
                                        layerId.toString(),
                                        objectId as number,
                                        shouldSearchItemRefresh
                                    );
                                }
                                props.onChange();
                                return updateResult;
                            } finally {
                                appContext?.featureEditsInFlight.end();
                                setIsSaving(false);
                            }
                        }}
                        errorMessage={errorMessage}
                        disabled={editsInFlight}
                        isSaving={isSaving}
                    />
                </div>
            );
        }

        return renderField();
    };

    const renderField = () => {
        if (!props.layer?.hasOwnProperty("fields")) {
            return <span className="feature-viewer-value">{codedValue}</span>;
        }
        const fieldInfo = (props.layer as FeatureLayer)?.fields.find((field) => field.name === props.field);

        if (!fieldInfo) {
            return <span className="feature-viewer-value">{codedValue}</span>;
        }

        if (fieldInfo.type === "date") {
            return <span className="feature-viewer-value">{formatDate(codedValue as number)}</span>;
        }

        return <span className="feature-viewer-value">{codedValue}</span>;
    };

    return (
        <div className="feature-field">
            {!props.isEditing && <span className="feature-viewer-value">{renderField()}</span>}
            {props.isEditing && renderEditableField()}
        </div>
    );
};

export default FeatureField;
